import { call, put, race, take, takeEvery } from 'typed-redux-saga';

import type { Product } from '@/domains/productDiscovery/algolia/interfaces/product';
import {
  ADD_TO_CART_FAILED,
  ADD_TO_CART_REQUEST,
  ADD_TO_CART_SUCCESS,
  addToCart,
  type AddToCartFailedAction,
  type AddToCartSuccessAction,
} from '@/domains/shoppingCart/AddToCart/AddToCart.actions';
import { APP_CODE } from '@/domains/shoppingCart/AddToCart/components/utils/constants';
import {
  addAlertMessage,
  AlertIDs,
  AlertSeverityType,
} from '@/domains/shoppingCart/FlashMessages/FlashMessages.actions';

import {
  addToCartError,
  addToCartOutOfStock,
  addToCartSuccess,
} from './translations';
import {
  ADD_TO_CART_WATCH_REQUEST,
  type AddToCartWatchAction,
} from './watchAddToCart.actions';

function* addToCartWatchSuccessSaga(productsIds?: Product['productId'][]) {
  const {
    payload: { savedProductIds },
  } = yield* take<AddToCartSuccessAction>(ADD_TO_CART_SUCCESS);

  const actualSavedProductIds = savedProductIds.filter((id) =>
    productsIds?.includes(`${id}`),
  );

  if (!productsIds || actualSavedProductIds.length > 0) {
    yield* put(
      addAlertMessage({
        id: AlertIDs.ADD_TO_CART_SUCCESS,
        severity: AlertSeverityType.SUCCESS,
        message: addToCartSuccess.format({
          amount: actualSavedProductIds.length,
        }) as string,
      }),
    );
  }
}

function* addToCartWatchFailedSaga(productsIds?: Product['productId'][]) {
  const { errors, payload } =
    yield* take<AddToCartFailedAction>(ADD_TO_CART_FAILED);

  const actualSavedProductIds =
    payload?.savedProductIds.filter((id) => productsIds?.includes(`${id}`)) ??
    [];

  if (
    !productsIds ||
    !payload ||
    payload.savedProductIds.length === 0 ||
    actualSavedProductIds.length > 0
  ) {
    yield* Array.isArray(errors) &&
    errors.some(
      (error) =>
        typeof error === 'object' &&
        error?.appCode === APP_CODE.PRODUCT_OUT_OF_STOCK,
    )
      ? put(
          addAlertMessage({
            id: AlertIDs.ADD_TO_CART_OUT_OF_STOCK,
            severity: AlertSeverityType.WARNING,
            message: addToCartOutOfStock.format({
              amount: actualSavedProductIds.length,
            }) as string,
          }),
        )
      : put(
          addAlertMessage({
            id: AlertIDs.ADD_TO_CART_FAILED,
            severity: AlertSeverityType.DANGER,
            message: addToCartError.format({
              amount: actualSavedProductIds.length,
            }) as string,
          }),
        );
  }
}

function* showAlertCartWatchSaga(productsIds: Product['productId'][]) {
  yield race([
    call(addToCartWatchSuccessSaga, productsIds),
    call(addToCartWatchFailedSaga, productsIds),
  ]);
}

export function createIsolatedWatchAddToCartSaga(
  productsIds: Product['productId'][],
) {
  return function* exportedWatchAddToCartSaga(): Generator<
    ReturnType<typeof takeEvery>,
    void,
    unknown
  > {
    yield takeEvery(
      ADD_TO_CART_WATCH_REQUEST,
      function* dispatchSaga(action: AddToCartWatchAction) {
        yield put(addToCart(action.payload));
        yield call(showAlertCartWatchSaga, productsIds);
      },
    );
  };
}

export function createWatchAddToCartSaga(productsIds: Product['productId'][]) {
  return function* exportedWatchAddToCartSaga(): Generator<
    ReturnType<typeof takeEvery>,
    void,
    unknown
  > {
    yield takeEvery(ADD_TO_CART_REQUEST, showAlertCartWatchSaga, productsIds);
  };
}
